import BorrowerDetailsTable from '@/components/BorrowerDetailsTable';
import Page from '@/components/Page';
import DataSummaryCard from '@/components/DataSummaryCard';
import TableSimple from '@/components/TableSimple';
import MortgageInfoBoxes from '@/components/MortgageInfoBoxes';
import { Group, Text, Button } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { roundNumber } from '@/helpers/number';
import { formatNumberWithCommas } from '@/helpers/string';
import { getMutationResult } from '@/helpers/query';
import { useGetPaymentLinkMutation } from '@/services/api';
import { updateUser } from '@/data/features/user/user';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import routes from '@/routes';
import CurrencyNisIcon from '@/assets/images/currencyNis.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './mortgageCycleSummary.module.css';

const MortgageCyclesSummary = () => {
  const { t } = useTranslation();
  const proposal = useSelector((state) => state.offer);
  const borrowersStore = proposal.borrower;
  const suffix = <CurrencyNisIcon style={{ width: '12px' }} />;
  const [apiGetPaymentLink] = useGetPaymentLinkMutation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = proposal || {};
  const before = proposal?.calculated?.totalsBeforeCalculation;
  const after = proposal?.calculated?.totalsAfterCalculation;
  const showRealPaymentPage = import.meta.env.VITE_APP_SHOW_REAL_PAYMENT_PAGE === 'true';

  const handleContinue = async () => {
    try {
      if (showRealPaymentPage) {
        const paymentData = {
          offerId: id,
        };
        const { data, isError } = await getMutationResult(paymentData, apiGetPaymentLink);

        if (isError) {
          notifications.show({
            title: t('globals.error.title'),
            message: t('globals.error.message'),
          });
          return;
        }
        dispatch(updateUser({ agreeToTerms: true, linkToPay: data.data.Url }));

        window.location.href = data.data.Url;
      } else {
        navigate(routes.pay.path);
      }
    } catch (error) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    }
  };

  const handleReturn = () => {
    navigate(routes.otp.path);
  };

  const details = [
    {
      label: t('pages.mortgageCyclesSummary.label.loanType'),
      value: proposal?.loan?.hebrew,
    },
    {
      label: t('pages.mortgageCyclesSummary.label.borrowerNumber'),
      value: borrowersStore.length,
    },
    {
      label: t('pages.mortgageCyclesSummary.label.loanAmount'),
      value: formatNumberWithCommas(
        roundNumber(after?.totalAmount)
      ),
      suffix,
    },
  ];

  const tableHead = [
    t('pages.mortgageCyclesSummary.table.head.month'),
    t('pages.mortgageCyclesSummary.table.head.remaining'),
    t('pages.mortgageCyclesSummary.table.head.monthlyPaymentWithoutInterest'),
    t('pages.mortgageCyclesSummary.table.head.monthlyPaymentInterest'),
    t('pages.mortgageCyclesSummary.table.head.totalPayment'),
  ];

  // Use mortgageCycle.repaymentTableData to populate the repayment table
  const tableBody = proposal?.repaymentTable?.map((item, index) => [
    item.monthIndex || index + 1, // month number
    `${formatNumberWithCommas(roundNumber(item.total?.remainingPrincipal || 0))} ₪`,
    `${formatNumberWithCommas(roundNumber(item.total?.principal || 0))} ₪`,
    `${formatNumberWithCommas(roundNumber(item.total?.interest || 0))} ₪`,
    `${formatNumberWithCommas(roundNumber(item.total?.totalPayment || 0))} ₪`,
  ]) || [];

  const disposalboardtableData = {
    head: tableHead,
    body: tableBody,
  };

  const actions = (
    <>
      <Button variant="transparent" onClick={handleReturn}>
        {t('globals.back')}
      </Button>
      <Button onClick={handleContinue} rightSection={<IconArrowLeft size={16} />}>
        {t('globals.continue')}
      </Button>
    </>
  );

  const side = <div></div>;

  return (
    <>
      <Page
        side={side}
        actions={actions}
        sideSize = 'none'
        title={t('pages.summary.title')}
        subtitle={t('pages.summary.subtitle')}
        extraSize="xl"
        maxWidth='full'
      >

        <Page.Head
          title={t('pages.mortgageCyclesSummary.title')}
          subtitle={t('pages.mortgageCyclesSummary.subtitle')}
        />

        <div>
          <DataSummaryCard details={details} />
        </div>

        <div>
          <h3>{t('pages.summary.title.borrowerDetails')}</h3>
          <BorrowerDetailsTable borrowers={borrowersStore} />
        </div>

        <Group mb="lg">
          <Text size="lg" fw={600} className={classes.title}>
            {t('pages.mortgageCyclesSummary.totalsComparison.title')}
          </Text>
        </Group>
        <div>
          <MortgageInfoBoxes
            totalsBeforeCalculation={before}
            effectiveTotalsAfter={after}
            currentMonthlyPayment={before?.newMonthlyPayment || 0}
          />
        </div>
        <div className={classes.summaryContainer}>
          <div className={classes.summaryCollapse}>
            <Page.Section
              title={t('pages.mortgageCyclesSummary.title.disposalBoardFor')}
              withCollapse
              withBackground
              startClose
              withBorder
            >
              <TableSimple data={disposalboardtableData} />
            </Page.Section>
          </div>
        </div>
      </Page>
    </>
  );
};

export default MortgageCyclesSummary;
